import React, { useMemo, useState, useCallback } from 'react';
import type { OrganizationMember, User } from '../../types.ts';
import { Pencil, Trash2 } from 'lucide-react';
import { Tooltip } from '../shared/Tooltip.tsx';
import { AdminModal } from '../shared/AdminModal.tsx';
import { DEFAULT_ORG_ROLE, ORG_ROLE_OPTIONS, formatOrgRoleLabel } from '../../orgRoles.ts';

export type OrganizationRoleOption = { value: string; label: string };

interface OrganizationMembersProps {
  members: OrganizationMember[];
  /** Current members as users (for display). */
  users: User[];
  isLoading: boolean;
  onRemoveUser: (userId: string) => Promise<unknown>;
  onUpdateRole: (args: { userId: string; payload: { role: string } }) => Promise<unknown>;
  /** When provided, enables inline email edit for members where canEditEmail is true. */
  onUpdateEmail?: (args: { userId: string; email: string }) => Promise<unknown>;
  /** When false, role cannot be edited for this member. Defaults to true. */
  canEditRole?: (member: OrganizationMember) => boolean;
  /** When false, email cannot be edited for this member. Defaults to false unless onUpdateEmail is set. */
  canEditEmail?: (member: OrganizationMember) => boolean;
  /** When false, member cannot be removed. Defaults to true (still respects is_default). */
  canRemove?: (member: OrganizationMember) => boolean;
  /** Role Access catalog options for the edit dropdown. Falls back to built-in four roles. */
  roleOptions?: OrganizationRoleOption[];
}

export const OrganizationMembers: React.FC<OrganizationMembersProps> = ({
  members,
  users,
  isLoading,
  onRemoveUser,
  onUpdateRole,
  onUpdateEmail,
  canEditRole = () => true,
  canEditEmail,
  canRemove = () => true,
  roleOptions,
}) => {
  const resolvedRoleOptions = useMemo(
    () => (roleOptions && roleOptions.length > 0 ? roleOptions : ORG_ROLE_OPTIONS),
    [roleOptions],
  );
  const roleLabel = useCallback(
    (role?: string | null) => {
      const key = (role ?? '').trim();
      if (!key) return '-';
      const match = resolvedRoleOptions.find((opt) => opt.value === key);
      if (match) return match.label;
      return formatOrgRoleLabel(key);
    },
    [resolvedRoleOptions],
  );
  const [editingRole, setEditingRole] = useState<{ userId: string; role: string } | null>(null);
  const [editRoleValue, setEditRoleValue] = useState<string>(DEFAULT_ORG_ROLE);
  const [editingEmail, setEditingEmail] = useState<{ userId: string; email: string } | null>(null);
  const [editEmailValue, setEditEmailValue] = useState('');
  const [removeUserTarget, setRemoveUserTarget] = useState<{ userId: string; displayName: string } | null>(null);

  const emailEditableFor = useCallback(
    (member: OrganizationMember) => {
      if (!onUpdateEmail) return false;
      if (canEditEmail) return canEditEmail(member);
      return true;
    },
    [onUpdateEmail, canEditEmail],
  );

  const handleUpdateRole = async (userId: string, role: string) => {
    try {
      await onUpdateRole({ userId, payload: { role } });
      setEditingRole(null);
    } catch (error) {
      console.error('Failed to update organization member role:', error);
      // Error toast is handled by the mutation hook.
    }
  };

  const handleUpdateEmail = async (userId: string, email: string) => {
    if (!onUpdateEmail) return;
    const trimmed = email.trim();
    if (!trimmed) return;
    try {
      await onUpdateEmail({ userId, email: trimmed });
      setEditingEmail(null);
    } catch (error) {
      console.error('Failed to update organization member email:', error);
      // Error toast is handled by the mutation hook.
    }
  };

  const handleRemoveUser = useCallback(async (userId: string) => {
    try {
      await onRemoveUser(userId);
      setRemoveUserTarget(null);
    } catch (error) {
      console.error('Failed to remove user from organization:', error);
      // Error toast is handled by the mutation hook.
    }
  }, [onRemoveUser]);

  const onConfirmRemoveUser = useCallback(() => {
    if (removeUserTarget) {
      handleRemoveUser(removeUserTarget.userId);
    }
  }, [removeUserTarget, handleRemoveUser]);

  if (isLoading) {
    return <div className="text-gray-500">Loading members...</div>;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Members</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Default</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {members.map((m) => {
              const u = m.user ?? users.find((x) => x.id === m.user_id);
              const displayName = u ? `${u.first_name} ${u.last_name}`.trim() || u.username : `User #${m.user_id}`;
              const email = u?.email ?? '-';
              const isEditing = editingRole?.userId === String(m.user_id);
              const isEditingEmail = editingEmail?.userId === String(m.user_id);
              const roleEditable = canEditRole(m);
              const emailEditable = emailEditableFor(m);
              const removable = canRemove(m) && !m.is_default;
              return (
                <tr key={m.id}>
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium text-gray-900">{displayName}</div>
                      {isEditingEmail ? (
                        <div className="mt-1 flex items-center gap-2">
                          <input
                            type="email"
                            value={editEmailValue}
                            onChange={(e) => setEditEmailValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleUpdateEmail(String(m.user_id), editEmailValue);
                              if (e.key === 'Escape') {
                                setEditingEmail(null);
                                setEditEmailValue('');
                              }
                            }}
                            className="min-w-[14rem] px-2 py-1 border rounded text-sm"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => handleUpdateEmail(String(m.user_id), editEmailValue)}
                            className="text-xs text-blue-600"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingEmail(null);
                              setEditEmailValue('');
                            }}
                            className="text-xs text-gray-500"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : emailEditable ? (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingEmail({ userId: String(m.user_id), email: u?.email ?? '' });
                            setEditEmailValue(u?.email ?? '');
                          }}
                          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-800"
                          title="Edit email"
                        >
                          <span>{email}</span>
                          <Pencil className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        </button>
                      ) : (
                        <div className="text-sm text-gray-500">{email}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <select
                          value={editRoleValue}
                          onChange={(e) => setEditRoleValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleUpdateRole(String(m.user_id), editRoleValue);
                            if (e.key === 'Escape') { setEditingRole(null); setEditRoleValue(DEFAULT_ORG_ROLE); }
                          }}
                          className="min-w-[11rem] px-2 py-1 border rounded text-sm"
                          autoFocus
                        >
                          {resolvedRoleOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleUpdateRole(String(m.user_id), editRoleValue)}
                          className="text-xs text-blue-600"
                        >
                          Save
                        </button>
                        <button onClick={() => { setEditingRole(null); setEditRoleValue(DEFAULT_ORG_ROLE); }} className="text-xs text-gray-500">
                          Cancel
                        </button>
                      </div>
                    ) : roleEditable ? (
                      <button
                        type="button"
                        onClick={() => {
                          const currentRole = (m.role ?? '').trim() || DEFAULT_ORG_ROLE;
                          setEditingRole({ userId: String(m.user_id), role: currentRole });
                          setEditRoleValue(currentRole);
                        }}
                        className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800"
                        title="Edit role"
                      >
                        <span>{roleLabel(m.role)}</span>
                        <Pencil className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      </button>
                    ) : (
                      <span className="text-gray-900">{roleLabel(m.role)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {m.is_default ? (
                      <span className="inline-flex items-center text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded">
                        Yes
                      </span>
                    ) : (
                      <span className="text-gray-500">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {u?.is_active === undefined ? (
                      <span className="text-gray-400">—</span>
                    ) : u?.is_active ? (
                      <span className="inline-flex items-center text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Tooltip
                      content={
                        m.is_default
                          ? 'Default user cannot be removed from organization'
                          : !removable
                            ? 'Cannot modify another organization admin'
                            : 'Remove user from this organization'
                      }
                      side="left"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (!removable) return;
                          setRemoveUserTarget({ userId: String(m.user_id), displayName });
                        }}
                        disabled={!removable}
                        className={`${
                          !removable
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-red-600 hover:text-red-800'
                        }`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </Tooltip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {members.length === 0 && (
        <div className="p-8 text-center text-gray-500">No members yet.</div>
      )}

      <AdminModal
        isOpen={!!removeUserTarget}
        onClose={() => setRemoveUserTarget(null)}
        title="Remove user from organization"
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Remove <span className="font-semibold">{removeUserTarget?.displayName}</span> from this organization?
            They will no longer have access to this organization.
          </p>
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={() => setRemoveUserTarget(null)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirmRemoveUser}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Remove
            </button>
          </div>
        </div>
      </AdminModal>
    </div>
  );
};
